/* Common Imports */

import { styled } from "@mui/system";
import { Dayjs } from "dayjs";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	setDataBeforeEditThunk,
	setPropertySnackbarThunk,
	setSectionToEditThunk,
	setSinglePropertyAboutLocationThunk,
	setSinglePropertyAboutThunk,
	setSinglePropertyAgeThunk,
	setSinglePropertyAmenitiesThunk,
	setSinglePropertyAvailabiltyThunk,
	setSinglePropertyCategoryTypeThunk,
	setSinglePropertyCurrencyThunk,
	setSinglePropertyDescriptionThunk,
	setSinglePropertyDeveloperNameThunk,
	setSinglePropertyEstablishedYearThunk,
	setSinglePropertyFloorNoThunk,
	setSinglePropertyFurnishingThunk,
	setSinglePropertyLandAreaThunk,
	setSinglePropertyLandFacingThunk,
	setSinglePropertyMaxBalconiesThunk,
	setSinglePropertyMaxBathroomsThunk,
	setSinglePropertyMaxBedroomsThunk,
	setSinglePropertyMaxCarpetAreaThunk,
	setSinglePropertyMaxFloorsThunk,
	setSinglePropertyMaxPriceThunk,
	setSinglePropertyMaxSuperBuiltUpAreaThunk,
	setSinglePropertyMinBalconiesThunk,
	setSinglePropertyMinBathroomsThunk,
	setSinglePropertyMinBedroomsThunk,
	setSinglePropertyMinCarpetAreaThunk,
	setSinglePropertyMinPriceThunk,
	setSinglePropertyMinSuperBuiltUpAreaThunk,
	setSinglePropertyNoOfSeatsThunk,
	setSinglePropertyNoOfUnitsThunk,
	setSinglePropertyOtherPlotDimensionsThunk,
	setSinglePropertyOverviewThunk,
	setSinglePropertyPlotDimensionsThunk,
	setSinglePropertyPossessionDateThunk,
	setSinglePropertySecurityDepositThunk,
	setSinglePropertySubTypeThunk,
	setSinglePropertyTagsThunk,
	setSinglePropertyTransactionTypeThunk,
	setSinglePropertyTypeThunk,
	setSinglePropertyUnitFacingThunk,
	setSinglePropertyVicinityThunk,
	UpdatePropertyDetailsThunk,
} from "../../../../../redux-magic/store";

/* Component Imports */

import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	IconButton,
	InputAdornment,
	Menu,
	MenuItem,
	Select,
	SelectChangeEvent,
	Skeleton,
	Snackbar,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Function Imports */

import PreparePriceString from "../../../../../lib/PreparePriceString";

/* Icon Imports */
import {
	CategoryOutlined,
	Close,
	EditOutlined,
	EventAvailableOutlined,
	HomeWorkOutlined,
	HourglassEmptyOutlined,
	MonetizationOnOutlined,
	PaymentsOutlined,
	ReceiptOutlined,
} from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CurrencyToNumberPipe from "../../../../../lib/CurrencyToNumberPipe";
import NumberToCurrencyPipe from "../../../../../lib/NumberToCurrencyPipe";
import { confirmSendForReview, skipNextSendForReviewConfirm } from "../../SendForReviewConfirm";
import { confirmDiscardChanges, useEditSnapshot } from "../../SectionDiscardConfirm";

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

/* Styled Components */

const PaperContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	gap: "0.5rem",
	overflow: "auto",
	boxShadow: "none",
	background: theme.palette.background.paper,
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		width: "50%",
		padding: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "0rem 0rem 0.25rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 0.5rem 0rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const TextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.6rem",
	width: "100%",
	padding: "0.5rem 0rem 0.5rem 0rem",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	gap: "0.5rem",
	width: "100%",
}));

const CustomText = styled(Typography)(({ theme }) => ({
	whiteSpace: "pre",
	fontWeight: "400",
	fontSize: "0.825rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const DetailsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	width: "100%",
}));

// const TagsRow = styled("div")(({ theme }) => ({
// 	display: "flex",
// 	flexDirection: "row",
// 	justifyContent: "flex-start",
// 	alignItems: "center",
// 	width: "100%",
// 	gap: "0.5rem",
// 	overflowX: "scroll",
// 	overflowY: "hidden",
// 	scrollbarWidth: "none",
// 	"&::-webkit-scrollbar": {
// 		display: "none",
// 	},
// }));

// const CustomChip = styled(Chip)(({ theme }) => ({
// 	// background: theme.palette.mode == "dark" ? "#2E3A46" : "#F5F5F5",
// 	color: theme.palette.mode == "dark" ? "#ffffff" : "#000000",
// 	fontWeight: "400",
// 	fontSize: "0.75rem",
// 	lineHeight: "1rem",
// 	// padding: "0.25rem 0.5rem 0.25rem 0.5rem",
// 	borderRadius: "0.25rem",
// 	height: "1.5rem",
// }));

const Snack = styled(Snackbar)(({ theme }) => ({
	// marginTop: "2.8rem",
	// [theme.breakpoints.down("sm")]: {
	// 	marginTop: "3.8rem",
	// },
}));

const CustomChip = styled(Button, {
	shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
	textAlign: "center",
	borderRadius: "8px",
	border: isSelected
		? "1px solid var(--Primary-New-Dark, #B05911)"
		: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
	backgroundColor: isSelected ? "#FFEBDA" : "#FFFFFF",
	color: isSelected ? "#B05911" : "inherit",
	fontWeight: 400,
	fontSize: "14px",
	padding: "0.5rem",
	"@media (pointer: fine)": {
		"&:hover": {
			backgroundColor: isSelected ? "#FFEBDA" : "#F5F5F5",
			border: isSelected
				? "1px solid var(--Primary-New-Dark, #B05911)"
				: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
		},
	},
	"@media (pointer: coarse)": {
		"&:hover": {
			backgroundColor: isSelected ? "#FFEBDA" : "#FFFFFF",
			border: isSelected
				? "1px solid var(--Primary-New-Dark, #B05911)"
				: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
		},
	},
	"&:active": {
		backgroundColor: isSelected ? "#FFEBDA" : "#FFFFFF",
		border: isSelected
			? "1px solid var(--Primary-New-Dark, #B05911)"
			: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
	},
	"&:focus": {
		backgroundColor: isSelected ? "#FFEBDA" : "#FFFFFF",
		border: isSelected
			? "1px solid var(--Primary-New-Dark, #B05911)"
			: "1px solid var(--Other-New-Divider, rgba(0, 0, 0, 0.12))",
	},
}));

const Text = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	fontWeight: "400",
	lineHeight: "1rem",
	padding: "3px 6px",
	textTransform: "capitalize",
	color: theme.palette.text.primary,
}));

interface security_deposit {
	min: number;
	max: number;
	negotiable: boolean;
	currency: string;
}

const PropertyCard = ({
	propertyData,
	property_id,
	property_transaction_type,
	property_category_type,
	property_type,
	property_subtype,
	property_min_price,
	property_max_price,
	property_currency,
	property_security_deposit,
	property_availability,
	property_possession_date,
	property_tags,
	property_unit_facing,
	property_land_facing,
	property_age,
	property_created_by,
	dispatch,
	session,
	negativeScroll,
	isEditor,
	profile_context,
	loading,
	section_to_edit,
	data_before_edit,
	property_details_snackbar,
	screenType,
}: {
	propertyData: LocalPropertyInterface;
	property_id: ObjectId | string;
	property_transaction_type: string;
	property_category_type: string;
	property_type: Array<string>;
	property_subtype: string;
	property_min_price: number;
	property_max_price: number;
	property_currency: string | undefined;
	property_security_deposit: security_deposit;
	property_availability: Array<string>;
	property_possession_date: string | Date;
	property_tags: Array<string>;
	property_unit_facing: Array<string>;
	property_land_facing: Array<string>;
	property_age: number;
	property_created_by: LocalPropertyInterface["created_by"];
	dispatch: Function;
	session: any;
	negativeScroll: boolean;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	loading: boolean;
	section_to_edit: string;
	data_before_edit: any;
	property_details_snackbar: boolean;
	screenType: string;
}) => {
	const theme = useTheme();

	const currencySymbol =
		property_currency === "AED" ? (
			<span style={{ fontFamily: "CurrencySymbols" }}>{String.fromCharCode(0xe001)}</span>
		) : (
			"\u20B9"
		);

	const [currencyMenuAnchor, setCurrencyMenuAnchor] = React.useState<null | HTMLElement>(null);

	const handleCurrencySelect = (currency: "INR" | "AED") => {
		dispatch(setSinglePropertyCurrencyThunk(currency));
		setCurrencyMenuAnchor(null);
	};

	/*

  & Let's declare the router. Here we are using the useRouter hook from nextjs.

  & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

  */

	const router = React.useRef(useRouter()).current;

	const localLang = {
		propertyDetails:
			router.locale === "kn-IN"
				? "ಪ್ರಾಪರ್ಟಿ ವಿವರಗಳು"
				: router.locale === "ar-AE"
					? property_transaction_type === "ofb"
						? "تفاصيل أو إف بي"
						: "تفاصيل العقار"
					: property_transaction_type === "ofb"
						? "OFB details"
						: "Property details",
		transactionType:
			router.locale === "kn-IN"
				? "ಟ್ರಾನ್ಸಾಕ್ಷನ್ ಟೈಪ್"
				: router.locale === "ar-AE"
					? "نوع المعاملة"
					: "Transaction Type",
		propertyCategory:
			router.locale === "kn-IN"
				? "ಪ್ರಾಪರ್ಟಿ ಕ್ಯಾಟಗರಿ"
				: router.locale === "ar-AE"
					? property_transaction_type === "ofb"
						? "فئة أو إف بي"
						: "فئة العقار"
					: property_transaction_type === "ofb"
						? "OFB category"
						: "Property category",
		propertyType:
			router.locale === "kn-IN"
				? "ಪ್ರಾಪರ್ಟಿ ಟೈಪ್"
				: router.locale === "ar-AE"
					? property_transaction_type === "ofb"
						? "نوع أو إف بي"
						: "نوع العقار"
					: property_transaction_type === "ofb"
						? "OFB type"
						: "Property type",
		securityDeposit:
			router.locale === "kn-IN"
				? "ಸಿಕ್ಯೂರಿಟಿ ಡೆಪಾಸಿಟ್"
				: router.locale === "ar-AE"
					? "عربون الأمان"
					: "Security deposit",
		priceText:
			router.locale === "kn-IN"
				? property_transaction_type != "rent"
					? "ಬೆಲೆ"
					: "ಬಾಡಿಗೆ"
				: router.locale === "ar-AE"
					? property_transaction_type != "rent"
						? "السعر"
						: "الإيجار"
					: property_transaction_type != "rent"
						? "Price"
						: "Rent",
		minimum_price:
			router.locale === "kn-IN" ? "ಕನಿಷ್ಠ ಬೆಲೆ" : router.locale === "ar-AE" ? "السعر الأدنى" : "Minimum price",
		maximum_price:
			router.locale === "kn-IN" ? "ಗರಿಷ್ಠ ಬೆಲೆ" : router.locale === "ar-AE" ? "السعر الأقصى" : "Maximum price",
		monthly_rent:
			router.locale === "kn-IN" ? "ತಿಂಗಳ ಬಾಡಿಗೆ" : router.locale === "ar-AE" ? "إيجار شهري" : "Monthly rent",
		propertyStatusTitle:
			router.locale === "kn-IN"
				? property_transaction_type === "rent"
					? "ಲಭ್ಯವಿದೆ"
					: "ಸ್ವಾಧೀನದ ದಿನಾಂಕ"
				: router.locale === "ar-AE"
					? property_transaction_type === "rent"
						? "التوافر"
						: "حالة العقار"
					: property_transaction_type === "rent"
						? "Availability"
						: "Property Status",
		propertyStatusEdit:
			router.locale === "kn-IN"
				? property_transaction_type === "rent"
					? "ಲಭ್ಯವಿದೆ"
					: "ಆಸ್ತಿ ಸ್ಥಿತಿ"
				: router.locale === "ar-AE"
					? property_transaction_type === "rent"
						? "متاح من"
						: "حالة العقار"
					: property_transaction_type === "rent"
						? "Available from"
						: "Property Status",
		posssesionDate:
			router.locale === "kn-IN"
				? property_transaction_type === "rent"
					? "ಲಭ್ಯವಿದೆ"
					: "ಪೋಷೆಶನ್ ದಿನಾಂಕ"
				: router.locale === "ar-AE"
					? property_transaction_type === "rent"
						? "متاح من"
						: "تاريخ الاستلام"
					: property_transaction_type === "rent"
						? "Available from"
						: "Possesion date",
		propertyAgeInYears:
			router.locale === "kn-IN"
				? "ಪ್ರಾಪರ್ಟಿ ವಯಸ್ಸು"
				: router.locale === "ar-AE"
					? "عمر العقار (بالسنوات)"
					: "Property age (in years)",
		propertyAge: router.locale === "kn-IN" ? "ಆಸ್ತಿ ವಯಸ್ಸು" : router.locale === "ar-AE" ? "عمر العقار" : "Property age",
		years: router.locale === "kn-IN" ? "ವರ್ಷಗಳು" : router.locale === "ar-AE" ? "سنوات" : "Years",
		snackValue:
			router.locale === "kn-IN"
				? "ಪ್ರಾಪರ್ಟಿ ಡೀಟೈಲ್ಸ್ ಅಪ್ಡೇಟ್ ಯಶಸ್ವಿಯಾಗಿ ಮಾಡಲಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "تم تحديث تفاصيل العقار بنجاح"
					: "Property details updated successfully",
		update: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "تحديث" : "Update",
		please_select_a_property_type:
			router.locale === "kn-IN"
				? "ದಯವಿಟ್ಟು ಒಂದು ಮಾಲೀಕರಣ ಪ್ರಕಾರವನ್ನು ಆಯ್ಕೆಮಾಡಿ"
				: router.locale === "ar-AE"
					? "يرجى اختيار نوع العقار"
					: "Please select a property type",
		value_is_more_than_maximum_price:
			router.locale === "kn-IN"
				? "ಮೌಲ್ಯ ಗರಿಷ್ಠ ಬೆಲೆಯಿಂದ ಕಡಿಮೆಯಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "القيمة أكبر من السعر الأقصى"
					: "Value is more than max price",
		value_is_less_than_minimum_price:
			router.locale === "kn-IN"
				? "ಮೌಲ್ಯ ಕನಿಷ್ಠ ಬೆಲೆಯಿಂದ ಕಡಿಮೆಯಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "القيمة أقل من السعر الأدنى"
					: "Value is less than min price",
		enter_a_valid_number:
			router.locale === "kn-IN"
				? "ಒಂದು ಮಾನ್ಯ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ"
				: router.locale === "ar-AE"
					? "أدخل رقمًا صالحًا"
					: "Enter a valid number",
		property_status_is_required:
			router.locale === "kn-IN"
				? "ಪ್ರಾಪರ್ಟಿ ಸ್ಥಿತಿ ಅಗತ್ಯವಿದೆ"
				: router.locale === "ar-AE"
					? "حالة العقار مطلوبة"
					: "Property status is required",
		sale: router.locale === "kn-IN" ? "ಮಾರಾಟ" : router.locale === "ar-AE" ? "بيع" : "Sale",
		rent: router.locale === "kn-IN" ? "ಬಾಡಿಗೆ" : router.locale === "ar-AE" ? "إيجار" : "Rent",
		resale: router.locale === "kn-IN" ? "ಮರಳಿ ಮಾರಾಟ" : router.locale === "ar-AE" ? "إعادة بيع" : "Resale",
		public_infrastructure:
			router.locale === "kn-IN"
				? "ಸಾರ್ವಜನಿಕ ಅಂಗಡಿ"
				: router.locale === "ar-AE"
					? "بنية تحتية عامة"
					: "Public infrastructure",
		joint_development:
			router.locale === "kn-IN" ? "ಸಹಕಾರಿ ಅಭಿವೃದ್ಧಿ" : router.locale === "ar-AE" ? "تطوير مشترك" : "Joint development",
		joint_venture:
			router.locale === "kn-IN" ? "ಸಹಕಾರಿ ಉದ್ಯಮ" : router.locale === "ar-AE" ? "مشروع مشترك" : "Joint venture",
		residential: router.locale === "kn-IN" ? "ನಿವಾಸಿ" : router.locale === "ar-AE" ? "سكني" : "Residential",
		commercial: router.locale === "kn-IN" ? "ವಾಣಿಜ್ಯ" : router.locale === "ar-AE" ? "تجاري" : "Commercial",
		industrial: router.locale === "kn-IN" ? "ಔದ್ಯೋಗಿಕ" : router.locale === "ar-AE" ? "صناعي" : "Industrial",
		agricultural: router.locale === "kn-IN" ? "ಕೃಷಿ" : router.locale === "ar-AE" ? "زراعي" : "Agricultural",
		warehousing: router.locale === "kn-IN" ? "ಗೋದಾಮಿಕೆ" : router.locale === "ar-AE" ? "تخزين" : "Warehousing",
		pleseSelectCategory:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ದಯವಿಟ್ಟು ಆಸ್ತಿ ವರ್ಗವನ್ನು ಆಯ್ಕೆಮಾಡಿ"
					: "Please select a property category",
		type: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಪ್ರಕಾರ*" : " Type*",
		pleaseSelectPropertyType:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ದಯವಿಟ್ಟು ಆಸ್ತಿ ಪ್ರಕಾರವನ್ನು ಆಯ್ಕೆಮಾಡಿ"
					: "Please select a property type",
		minPrice: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಕನಿಷ್ಠ ಬೆಲೆ*" : "Minimum Price*",
		valueMoreTnanMaxPrice:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಮೌಲ್ಯವು ಗರಿಷ್ಠ ಬೆಲಿಗಿಂತ ಹೆಚ್ಚು ಇದೆ"
					: "Value is more than max price",
		maxPrice: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಗರಿಷ್ಠ ಬೆಲೆ*" : "Maximum Price*",
		valueLessThanMinPrice:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಮೌಲ್ಯವು ಕನಿಷ್ಠ ಬೆಲಿಗಿಂತ ಕಡಿಮೆಯಿದೆ"
					: "Value is less than min price",
		price: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಬೆಲೆ*" : "Price*",
		currency: router.locale === "ar-AE" ? "العملة" : router.locale === "kn-IN" ? "ಕರೆನ್ಸಿ" : "Currency",
		monthlyRent: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ತಿಂಗಳಿನ ಬಾಡಿಗೆ*" : "Monthly Rent*",
		enterAValidNumber:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಒಂದು ಮಾನ್ಯ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ"
					: "Enter a valid number",

		propertyStatus: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಆಸ್ತಿ ಸ್ಥಿತಿ*" : "Property Status*",
		availableFrom: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಲಭ್ಯವಿದೆ ಇಂದಿಂದ*" : "Available from*",
		pleaseSelectPropertyStatus:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ದಯವಿಟ್ಟು ಆಸ್ತಿ ಸ್ಥಿತಿಯನ್ನು ಆಯ್ಕೆಮಾಡಿ"
					: "Please select property status",
		possessionDate:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸ್ವಾಮಿತ್ವ ದಿನಾಂಕ*" : "Possession Date*",
		facing: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಬಾಗಿಲು" : "Facing",
		apartment: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಅಪಾರ್ಟ್‌ಮೆಂಟ್" : "Apartment",
		indipendentHouse: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸ್ವತಂತ್ರ ಮನೆ" : "Independent House",
		villa: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ವಿಲ್ಲಾ" : "Villa",
		plot: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಪ್ಲಾಟ್" : "Plot",
		landResidential:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಭೂಮಿ (ವಾಸಸ್ಥಳ) " : "Land (Residential)",
		coLiving: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಕೋ-ಲಿವಿಂಗ್/ಪಿಜಿ" : "Co-living/PG",
		rowHouse: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ರೋ ಹೌಸ್" : "Row House",
		builderFloorApt:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಬಿಲ್ಡರ್ ಫ್ಲೋರ್ ಅಪಾರ್ಟ್‌ಮೆಂಟ್"
					: "Builder Floor Apartment",
		penthouse: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಪೆಂಟ್ಹೌಸ್" : "Penthouse",
		studioApartment:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸ್ಟೂಡಿಯೊ ಅಪಾರ್ಟ್‌ಮೆಂಟ್" : "Studio Apartment",
		landCommercial:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಭೂಮಿ (ವಾಣಿಜ್ಯಿಕ)" : "Land (Commercial)",
		buildingCommercial:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಕಟ್ಟಡ (ವಾಣಿಜ್ಯಿಕ)" : "Building (Commercial)",
		buildingRecidential:
			router.locale === "ar-Ae" ? "" : router.locale === "kn-IN" ? "ಕಟ್ಟಡ (ವಾಸಿಕ)" : "Building (Residential)",
		shop: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ದೋಕಾನ" : "Shop",
		officeSpace: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಕಾರ್ಯಾಲಯ ಸ್ಥಳ" : "Office Space",
		coWorking: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಕೋ-ವರ್ಕಿಂಗ್" : "Co-working",
		plotCommercial:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಭೂಮಿ (ವಾಣಿಜ್ಯಿಕ)" : "Plot (Commercial)",
		landAgricultural:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಭೂಮಿ (ಕೃಷಿ)" : "Land (Agricultural)",
		farmHouse: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಫಾರ್ಮ್ ಹೌಸ್" : "Farm House",
		farmPlot: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಫಾರ್ಮ್ ಪ್ಲಾಟ್" : "Farm Plot",
		landIndustrial: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಭೂಮಿ (ಕೈಗಾರಿಕ)" : "Land (Industrial)",
		factory: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಕಾರ್ಖಾನೆ" : "Factory",
		readyToMoveIn:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಹಾಜರಾಗಿ ಸ್ಥಳಾಂತರಿಸಲು ಸಿದ್ಧ" : "Ready to move in",
		underConstruction:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ನಿರ್ಮಾಣದಡಿಯಲ್ಲಿ" : "Under construction",
		builtToSuit:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸೂಟ್ ಮಾಡಲು ನಿರ್ಮಿಸಲಾಗಿದೆ" : "Built to suit",
		bareShell: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಬೇರ್ ಶೆಲ್" : "Bare shell",
		immediately: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ತಕ್ಷಣ" : "Immediately",
		selectDate: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ದಿನಾಂಕ ಆಯ್ಕೆಮಾಡಿ" : "Select date",
		readyForRegistration:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ನೋಂದಣಿಗೆ ಸಿದ್ಧ" : "Ready for registration",
		north: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಉತ್ತರ" : "North",
		south: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ದಕ್ಷಿಣ" : "South",
		east: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಪೂರ್ವ" : "East",
		west: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಪಶ್ಚಿಮ" : "West",
		northEast: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಉತ್ತರ ಪೂರ್ವ" : "North east",
		northWest: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಉತ್ತರ ಪಶ್ಚಿಮ" : "North west",
		southEast: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ದಕ್ಷಿಣ ಪೂರ್ವ" : "South east",
		southWest: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ದಕ್ಷಿಣ ಪಶ್ಚಿಮ" : "South west",
	};

	const [isEditing, setIsEditing] = React.useState(false);
	const isSectionDirty = useEditSnapshot(isEditing);

	const newDelimitedSearchString = propertyData.search_metrics
		? propertyData.search_metrics.search_string_delimiter.split("ߐ")
		: [];

	const [submitDisabled, setSubmitDisabled] = React.useState<boolean>(false);

	const [errorMinPrice, setErrorMinPrice] = React.useState<boolean>(false);

	const [errorMaxPrice, setErrorMaxPrice] = React.useState<boolean>(false);

	const [errorPropertyAge, setErrorPropertyAge] = React.useState<boolean>(false);

	const [errorTransactionType, setErrorTransactionType] = React.useState<boolean>(false);

	const [errorCategoryType, setErrorCategoryType] = React.useState<boolean>(false);

	const [errorPropertyType, setErrorPropertyType] = React.useState<boolean>(false);

	const [errorAvailability, setErrorAvailability] = React.useState<boolean>(false);

	const [errorPossessionDate, setErrorPossessionDate] = React.useState<boolean>(false);

	const [isDatePickerOpen, setIsDatePickerOpen] = React.useState<boolean>(false);

	const [load, setLoad] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (section_to_edit !== "property-details") {
			setIsEditing(false);
		}
	}, [data_before_edit, section_to_edit]);

	const profile_context_user = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	if (loading) {
		return (
			<PaperContainer>
				<Contain>
					<Heading>
						<Skeleton
							animation="wave"
							variant="text"
							width="15rem"
						/>
					</Heading>
				</Contain>
				<TextContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="20rem"
					/>
				</TextContainer>
				<TextContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="20rem"
					/>
				</TextContainer>
				<TextContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="20rem"
					/>
				</TextContainer>
				<TextContainer>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="20rem"
					/>
				</TextContainer>
			</PaperContainer>
		);
	}

	/* Functions */

	const handleChangePropertyType = async (event: SelectChangeEvent<typeof property_type>) => {
		const {
			target: { value },
		} = event;
		if (value === "Studio Apartment") {
			dispatch(setSinglePropertyTypeThunk(["Studio Apartment"]));
			dispatch(setSinglePropertySubTypeThunk(""));
			dispatch(setSinglePropertyMinPriceThunk(0));
			dispatch(setSinglePropertyMaxPriceThunk(0));
			dispatch(setSinglePropertySecurityDepositThunk(0));
			dispatch(setSinglePropertyAvailabiltyThunk([]));
			dispatch(setSinglePropertyPossessionDateThunk(""));
			dispatch(setSinglePropertyAgeThunk(0));
			dispatch(setSinglePropertyUnitFacingThunk([]));
			dispatch(setSinglePropertyLandFacingThunk([]));
			dispatch(setSinglePropertyPlotDimensionsThunk([]));
			dispatch(setSinglePropertyMinBedroomsThunk(0.5));
			dispatch(setSinglePropertyMaxBedroomsThunk(0.5));
			dispatch(setSinglePropertyMaxBathroomsThunk(0.5));
		} else {
			dispatch(
				setSinglePropertyTypeThunk(
					// On autofill we get a stringified value.
					typeof value === "string" ? value.split(",") : value,
				),
			);
			dispatch(setSinglePropertySubTypeThunk(""));
			dispatch(setSinglePropertyMinPriceThunk(0));
			dispatch(setSinglePropertyMaxPriceThunk(0));
			dispatch(setSinglePropertySecurityDepositThunk(0));
			dispatch(setSinglePropertyAvailabiltyThunk([]));
			dispatch(setSinglePropertyPossessionDateThunk(""));
			dispatch(setSinglePropertyAgeThunk(0));
			dispatch(setSinglePropertyUnitFacingThunk([]));
			dispatch(setSinglePropertyLandFacingThunk([]));
			dispatch(setSinglePropertyPlotDimensionsThunk([]));
			dispatch(setSinglePropertyMinBedroomsThunk(0));
			dispatch(setSinglePropertyMaxBedroomsThunk(0));
			dispatch(setSinglePropertyMaxBathroomsThunk(0));
			dispatch(setSinglePropertyMinBathroomsThunk(0));
			dispatch(setSinglePropertyMinBalconiesThunk(0));
			dispatch(setSinglePropertyMaxBalconiesThunk(0));
		}
		setErrorPropertyType(false);
	};

	const handleChangeAvailability = (event: SelectChangeEvent<typeof property_availability>) => {
		const {
			target: { value },
		} = event;
		if (value === "immediately" || value === "ready_to_move_in" || value === "ready_for_construction") {
			dispatch(setSinglePropertyPossessionDateThunk(new Date().toISOString().slice(0, 10)));
			dispatch(
				setSinglePropertyAvailabiltyThunk(
					// On autofill we get a stringified value.
					typeof value === "string" ? value.split(",") : value,
				),
			);
			dispatch(setSinglePropertyPossessionDateThunk(""));
		} else {
			dispatch(
				setSinglePropertyAvailabiltyThunk(
					// On autofill we get a stringified value.
					typeof value === "string" ? value.split(",") : value,
				),
			);
		}
		setErrorAvailability(false);
	};

	const handleChangeUnitFacing = (event: SelectChangeEvent<typeof property_unit_facing>) => {
		const {
			target: { value },
		} = event;
		dispatch(
			setSinglePropertyUnitFacingThunk(
				// On autofill we get a stringified value.
				typeof value === "string" ? value.split(",") : value,
			),
		);
	};

	// const handleChangeLandFacing = (event: SelectChangeEvent<typeof property_land_facing>) => {
	// 	const {
	// 		target: { value },
	// 	} = event;
	// 	dispatch(
	// 		setSinglePropertyLandFacingThunk(
	// 			// On autofill we get a stringified value.
	// 			typeof value === "string" ? value.split(",") : value,
	// 		),
	// 	);
	// };

	// const handleChangeTags = (event: React.ChangeEvent<HTMLInputElement>) => {
	// 	const {
	// 		target: { value },
	// 	} = event;
	// 	dispatch(
	// 		setSinglePropertyTagsThunk(
	// 			// On autofill we get a stringified value.
	// 			typeof value === "string" ? value.split(",") : value,
	// 		),
	// 	);
	// };

	const propertyDetailsContainer = document.getElementById("property-details");

	const availability: Array<string> = property_availability.map((item: string) =>
		item.toLowerCase().replace(/\s+/g, "_"),
	);

	const submitForm = async () => {
		/* Saving unlists the listing until it is approved again: say so first (gap A3, the app's prompt). */
		if (!(await confirmSendForReview())) return;
		setLoad(true);
		await dispatch(
			UpdatePropertyDetailsThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				property_id: property_id,
				delimited_search_string: newDelimitedSearchString.join("ߐ"),
				search_string: newDelimitedSearchString.join("ߐ"),
				transaction_type: property_transaction_type,
				property_category: property_category_type,
				property_type: property_type,
				property_subtype: property_subtype,
				min_price: property_min_price,
				max_price: property_max_price,
				currency: property_currency === "AED" ? "AED" : "INR",
				security_deposit: property_security_deposit.max,
				possession_date: property_possession_date,
				property_age: property_age,
				availability: availability,
				unit_facing: property_unit_facing,
				land_facing: property_land_facing,
				tag: property_tags,
				furnishing: propertyData.furnished ? propertyData.furnished : [],
				min_bedrooms: propertyData.bhk.min ? propertyData.bhk.min : 0,
				max_bedrooms: propertyData.bhk.max ? propertyData.bhk.max : 0,
				min_bathrooms: propertyData.bathroom.min ? propertyData.bathroom.min : 0,
				max_bathrooms: propertyData.bathroom.max ? propertyData.bathroom.max : 0,
				min_balconies: propertyData.balconies.min ? propertyData.balconies.min : 0,
				max_balconies: propertyData.balconies.max ? propertyData.balconies.max : 0,
				total_floors: propertyData.floors.max ? propertyData.floors.max : 1,
				floor_no: propertyData.floor_no.max ? propertyData.floor_no.max : 1,
				no_of_seats: propertyData.no_of_seats.max ? propertyData.no_of_seats.max : 0,
				no_of_units: propertyData.no_of_units.max ? propertyData.no_of_units.max : 0,
				land_area: propertyData.area.land_area.max.acre ? propertyData.area.land_area.max.acre : 0,
				land_area_unit: "acre",
				min_super_built_up_area: propertyData.area.super_built_up_area.min.ft
					? propertyData.area.super_built_up_area.min.ft
					: 0,
				max_super_built_up_area: propertyData.area.super_built_up_area.max.ft
					? propertyData.area.super_built_up_area.max.ft
					: 0,
				super_built_up_area_unit: "sqft",
				min_carpet_area: propertyData.area.carpet_area.min.ft ? propertyData.area.carpet_area.min.ft : 0,
				max_carpet_area: propertyData.area.carpet_area.max.ft ? propertyData.area.carpet_area.max.ft : 0,
				carpet_area_unit: "sqft",
				plot_dimensions: propertyData.plot_dimensions ? propertyData.plot_dimensions : [],
				other_plot_dimensions: propertyData.other_plot_dimensions ? propertyData.other_plot_dimensions : [],
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: propertyData.created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				profile_context: profile_context,
			}),
		);
		dispatch(setSectionToEditThunk(""));
		dispatch(setDataBeforeEditThunk({}));
		setIsEditing(false);
		propertyDetailsContainer
			? propertyDetailsContainer.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "start",
				})
			: "";
		setLoad(false);
	};

	const pTypes: Array<{ value: string; name: string }> = [];
	const AvailItems: Array<{ value: string; name: string }> = [];
	// const subtype_options: Array<string> = [];

	const residential = [
		{ value: "Apartment", name: localLang.apartment },
		{ value: "Independent House", name: localLang.indipendentHouse },
		{ value: "Villa", name: localLang.villa },
		{ value: "Plot", name: localLang.plot },
		{ value: "Land (Residential)", name: localLang.landResidential },
		{ value: "Co-living/PG", name: localLang.coLiving },
		{ value: "Row House", name: localLang.rowHouse },
		{ value: "Builder Floor Apartment", name: localLang.builderFloorApt },
		{ value: "Penthouse", name: localLang.penthouse },
		{ value: "Studio Apartment", name: localLang.studioApartment },
		{ value: "Building (Residential)", name: localLang.buildingRecidential },
	];

	const residentialWOland = [
		{ value: "Apartment", name: localLang.apartment },
		{ value: "Independent House", name: localLang.indipendentHouse },
		{ value: "Villa", name: localLang.villa },
		{ value: "Plot", name: localLang.plot },
		{ value: "Co-living/PG", name: localLang.coLiving },
		{ value: "Row House", name: localLang.rowHouse },
		{ value: "Builder Floor Apartment", name: localLang.builderFloorApt },
		{ value: "Penthouse", name: localLang.penthouse },
		{ value: "Studio Apartment", name: localLang.studioApartment },
		{ value: "Building (Residential)", name: localLang.buildingRecidential },
	];

	const commercial = [
		{ value: "Land (Commercial)", name: localLang.landCommercial },
		{ value: "Building (Commercial)", name: localLang.buildingCommercial },
		{ value: "Shop", name: localLang.shop },
		{ value: "Office Space", name: localLang.officeSpace },
		{ value: "Co-working", name: localLang.coWorking },
		{ value: "Plot (Commercial)", name: localLang.plotCommercial },
	];

	const commercialWOland = [
		{ value: "Building (Commercial)", name: localLang.buildingCommercial },
		{ value: "Shop", name: localLang.shop },
		{ value: "Office Space", name: localLang.officeSpace },
		{ value: "Co-working", name: localLang.coWorking },
		{ value: "Plot (Commercial)", name: localLang.plotCommercial },
	];

	const agricultural = [
		{ value: "Land (Agricultural)", name: localLang.landAgricultural },
		{ value: "Farm House", name: localLang.farmHouse },
		{ value: "Farm Plot", name: localLang.farmPlot },
	];

	const agriculturalWOland = [
		{ value: "Farm House", name: localLang.farmHouse },
		{ value: "Farm Plot", name: localLang.farmPlot },
	];

	const industrial = [
		{ value: "Land (Industrial)", name: localLang.landIndustrial },
		{ value: "Factory", name: localLang.factory },
		{ value: "Mining", name: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಖನಿ" : "Mining" },
	];

	const industrialWOland = [{ value: "Factory", name: localLang.factory }];

	const Availabilty = [
		{ value: "Ready to move in", name: localLang.readyToMoveIn },
		{ value: "Under construction", name: localLang.underConstruction },
		{ value: "Built to suit", name: localLang.builtToSuit },
		{ value: "Bare shell", name: localLang.bareShell },
	];

	const RentAvailabilty = [
		{ value: "Immediately", name: localLang.immediately },
		{ value: "Select date", name: localLang.selectDate },
	];

	const ApartmentAvailabilty = [
		{ value: "Ready to move in", name: localLang.readyToMoveIn },
		{ value: "Under construction", name: localLang.underConstruction },
	];

	const PlotResidentialAvailability = [
		{ value: "Ready for registration", name: localLang.readyForRegistration },
		{ value: "Under construction", name: localLang.underConstruction },
	];

	const WarehousingAvailabilty = [
		{ value: "Ready to move in", name: localLang.readyToMoveIn },
		{ value: "Under construction", name: localLang.underConstruction },
		{ value: "Built to suit", name: localLang.builtToSuit },
	];

	const Facing = [
		{ value: "North", name: localLang.north },
		{ value: "South", name: localLang.south },
		{ value: "East", name: localLang.east },
		{ value: "West", name: localLang.west },
		{ value: "North east", name: localLang.northEast },
		{ value: "North west", name: localLang.northWest },
		{ value: "South east", name: localLang.southEast },
		{ value: "South west", name: localLang.southWest },
	];
	/* Property Type options */

	switch (property_transaction_type) {
		case "resale":
			switch (property_category_type) {
				case "residential":
					pTypes.push(...residentialWOland);
					break;
				case "commercial":
					pTypes.push(...commercialWOland);
					break;
				case "agricultural":
					pTypes.push(...agriculturalWOland);
					break;
				case "industrial":
					pTypes.push(...industrialWOland);
					break;
				default:
					pTypes.push();
					break;
			}
			break;
		default:
			switch (property_category_type) {
				case "residential":
					pTypes.push(...residential);
					break;
				case "commercial":
					pTypes.push(...commercial);
					break;
				case "agricultural":
					pTypes.push(...agricultural);
					break;
				case "industrial":
					pTypes.push(...industrial);
					break;
				default:
					pTypes.push();
					break;
			}
	}

	/* Availability / Property Status options */

	switch (property_type[0]) {
		case "Apartment":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Studio Apartment":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Independent House":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Villa":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Plot":
			AvailItems.push(...PlotResidentialAvailability);
			break;
		case "Farm Plot":
			AvailItems.push(...PlotResidentialAvailability);
			break;
		case "Plot (Commercial)":
			AvailItems.push(...PlotResidentialAvailability);
			break;
		case "Co-living/PG":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Row House":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Builder Floor Apartment":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Penthouse":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Co-working":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Building (Commercial)":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Building (Residential)":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Shop":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Office Space":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Farm House":
			AvailItems.push(...ApartmentAvailabilty);
			break;
		case "Warehouse":
			AvailItems.push(...WarehousingAvailabilty);
			break;
		case "Factory":
			AvailItems.push(...WarehousingAvailabilty);
			break;
		default:
			AvailItems.push(...Availabilty);
	}

	/* Property Subtype options */

	// switch (property_type[0]) {
	// 	case "Land (Commercial)":
	// 		subtype_options.push(...land_subtypes);
	// 		break;
	// 	case "Plot (Commercial)":
	// 		subtype_options.push(...land_subtypes);
	// 		break;
	// 	case "Building":
	// 		subtype_options.push(...all_subtypes);
	// 		break;
	// 	case "Shop":
	// 		subtype_options.push(...all_subtypes);
	// 		break;
	// 	case "Office Space":
	// 		subtype_options.push(...office_subtypes);
	// 		break;
	// 	case "Co-working":
	// 		subtype_options.push(...office_subtypes);
	// 		break;
	// 	case "Warehouse":
	// 		subtype_options.push(...warehouse_subtypes);
	// 		break;
	// 	case "Land (Industrial)":
	// 		subtype_options.push(...land_subtypes);
	// 		break;
	// 	case "Factory":
	// 		subtype_options.push(...sez_subtypes);
	// 		break;
	// 	case "Mining":
	// 		subtype_options.push(...sez_subtypes);
	// 		break;
	// 	default:
	// 		subtype_options.push();
	// 		break;
	// }

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	const creator_user_id: string | ObjectId | UserInterface | BusinessProfileInterface = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: (property_created_by.business_profile_id as string)
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: (property_created_by.user_id as string)
		: "";

	const is_user_creator: boolean =
		profile_context_user !== ""
			? creator_user_id !== ""
				? profile_context_user === creator_user_id
					? true
					: false
				: false
			: false;

	const handleCategoryClick = (value: string) => {
		if (value === "warehousing") {
			dispatch(setSinglePropertyTypeThunk(["Warehouse"]));
		} else {
			dispatch(setSinglePropertyTypeThunk([]));
		}
		dispatch(setSinglePropertyCategoryTypeThunk(value));
		dispatch(setSinglePropertySubTypeThunk(""));
		dispatch(setSinglePropertyMinPriceThunk(0));
		dispatch(setSinglePropertyMaxPriceThunk(0));
		dispatch(setSinglePropertySecurityDepositThunk(0));
		dispatch(setSinglePropertyAvailabiltyThunk([]));
		dispatch(setSinglePropertyPossessionDateThunk(""));
		dispatch(setSinglePropertyAgeThunk(0));
		dispatch(setSinglePropertyUnitFacingThunk([]));
		dispatch(setSinglePropertyLandFacingThunk([]));
		// You can reset UI fields below as needed
	};

	return (
		<PaperContainer id={screenType + "_spp_property-details"}>
			<Snack
				open={property_details_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.snackValue}
				</Alert>
			</Snack>

			{/* Icon */}

			<Contain>
				{/* <Heading>{(property_transaction_type === "ofb" ? "OFB" : "Property") + " details"}</Heading> */}
				<Heading>{localLang.propertyDetails}</Heading>
				{is_user_creator ||
				(logged_user_access.includes("admin") &&
					logged_user_access.includes("property") &&
					propertyData.assigned_to.includes(logged_user)) ||
				logged_user_access.includes("super_admin") ||
				isEditor ? (
					<React.Fragment>
						{!isEditing ? (
							<IconButton
								id={screenType + "_spp_property_details_edit"}
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									dispatch(setSectionToEditThunk("property-details"));
									if (section_to_edit !== "property-details") {
										switch (section_to_edit) {
											case "property-config":
												dispatch(setSinglePropertyFurnishingThunk(data_before_edit.property_furnishing_status));
												dispatch(setSinglePropertyLandAreaThunk(data_before_edit.property_area));
												dispatch(setSinglePropertyMaxBalconiesThunk(data_before_edit.property_balconies_max));
												dispatch(setSinglePropertyMaxBathroomsThunk(data_before_edit.property_bathroom_max));
												dispatch(setSinglePropertyMaxBedroomsThunk(data_before_edit.property_bhk_max));
												dispatch(setSinglePropertyMaxCarpetAreaThunk(data_before_edit.property_carpet_area_max));
												dispatch(setSinglePropertyMaxFloorsThunk(data_before_edit.property_floors_max));
												dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(data_before_edit.super_builtup_max));
												dispatch(setSinglePropertyMinBalconiesThunk(data_before_edit.property_balconies_min));
												dispatch(setSinglePropertyMinBathroomsThunk(data_before_edit.property_bathroom_min));
												dispatch(setSinglePropertyMinBedroomsThunk(data_before_edit.property_bedroom_min));
												dispatch(setSinglePropertyMinCarpetAreaThunk(data_before_edit.property_carpet_area_mix));
												dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(data_before_edit.super_builtup_min));
												dispatch(setSinglePropertyNoOfUnitsThunk(data_before_edit.property_no_of_units));
												dispatch(setSinglePropertyNoOfSeatsThunk(data_before_edit.property_no_of_seats));
												dispatch(setSinglePropertyFloorNoThunk(data_before_edit.property_floor_no));
												dispatch(setSinglePropertyOtherPlotDimensionsThunk(data_before_edit.other_plot_dimensions));
												dispatch(setSinglePropertyPlotDimensionsThunk(data_before_edit.property_plot_dimensions));
												break;
											case "property-overview":
												dispatch(setSinglePropertyOverviewThunk(data_before_edit));
												break;
											case "about-property":
												dispatch(setSinglePropertyAboutThunk(data_before_edit));
												break;
											case "property-amenities":
												dispatch(setSinglePropertyAmenitiesThunk(data_before_edit));
												break;
											case "property-developer":
												dispatch(setSinglePropertyDeveloperNameThunk(data_before_edit.name));
												dispatch(setSinglePropertyDescriptionThunk(data_before_edit.description));
												dispatch(setSinglePropertyEstablishedYearThunk(data_before_edit.year_est));
												break;
											case "property-location":
												dispatch(setSinglePropertyAboutLocationThunk(data_before_edit));
												break;
											case "property-vicinity":
												dispatch(setSinglePropertyVicinityThunk(data_before_edit));
												break;
											default:
												break;
										}
										dispatch(setSectionToEditThunk("property-details"));
									}
									setIsEditing(true);
									dispatch(
										setDataBeforeEditThunk({
											property_category_type: property_category_type,
											property_transaction_type: property_transaction_type,
											property_type: property_type,
											property_subtype: property_subtype,
											property_min_price: property_min_price,
											property_max_price: property_max_price,
											property_currency: property_currency,
											property_security_deposit: property_security_deposit.max,
											property_availability: property_availability,
											property_possession_date: property_possession_date,
											property_tags: property_tags,
											property_unit_facing: property_unit_facing,
											property_land_facing: property_land_facing,
											property_age: property_age,
										}),
									);
								}}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						) : (
							<IconButton
								id={screenType + "_spp_property_details_close_button"}
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={async () => {
									/* Unsaved changes ask first (3 Nov 2026, the app's "Discard changes?"). */
									if (isSectionDirty()) {
										const answer = await confirmDiscardChanges();
										if (answer === "keep") return;
										if (answer === "save") {
											skipNextSendForReviewConfirm();
											await submitForm();
											return;
										}
									}
									setIsEditing(false);
									dispatch(setSinglePropertyCategoryTypeThunk(data_before_edit.property_category_type));
									dispatch(setSinglePropertyTransactionTypeThunk(data_before_edit.property_transaction_type));
									dispatch(setSinglePropertyTypeThunk(data_before_edit.property_type));
									dispatch(setSinglePropertySubTypeThunk(data_before_edit.property_subtype));
									dispatch(setSinglePropertyMinPriceThunk(data_before_edit.property_min_price));
									dispatch(setSinglePropertyMaxPriceThunk(data_before_edit.property_max_price));
									dispatch(setSinglePropertyCurrencyThunk(data_before_edit.property_currency));
									dispatch(setSinglePropertySecurityDepositThunk(data_before_edit.property_security_deposit));
									dispatch(setSinglePropertyAvailabiltyThunk(data_before_edit.property_availability));
									dispatch(setSinglePropertyPossessionDateThunk(data_before_edit.property_possession_date));
									dispatch(setSinglePropertyTagsThunk(data_before_edit.property_tags));
									dispatch(setSinglePropertyUnitFacingThunk(data_before_edit.property_unit_facing));
									dispatch(setSinglePropertyLandFacingThunk(data_before_edit.property_land_facing));
									dispatch(setSinglePropertyAgeThunk(data_before_edit.property_age));
									setErrorAvailability(false);
									setErrorCategoryType(false);
									setErrorPropertyType(false);
									setErrorTransactionType(false);
									setErrorMinPrice(false);
									setErrorMaxPrice(false);
									setErrorPropertyAge(false);
								}}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						)}
					</React.Fragment>
				) : null}
			</Contain>

			{/* Transaction Type */}

			{property_transaction_type !== "ofb" ? (
				<TextContainer>
					{!isEditing && (
						<ReceiptOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.transactionType + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_transaction_type
									? property_transaction_type == "buy"
										? "Sale"
										: property_transaction_type.charAt(0).toUpperCase() + property_transaction_type.slice(1)
									: "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						<FormControl
							sx={{
								width: "100%",
								// margin: "0.5rem 0 0 0",
								gap: "0.5rem",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<ReceiptOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.transactionType + "*"}
							</Typography>

							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									flexWrap: "wrap",
								}}
							>
								{[
									{ label: localLang.sale, value: "buy" },
									{ label: localLang.rent, value: "rent" },
									{ label: localLang.resale, value: "resale" },
								].map((option) => {
									const isSelected = option && property_transaction_type === option.value;

									return (
										<CustomChip
											key={option && option.value}
											disableRipple
											disableTouchRipple
											disableFocusRipple
											isSelected={isSelected}
											onClick={() => {
												dispatch(setSinglePropertyTransactionTypeThunk(option && option.value));
												dispatch(setSinglePropertyTypeThunk([]));
												dispatch(setSinglePropertySubTypeThunk(""));
												dispatch(setSinglePropertyMinPriceThunk(0));
												dispatch(setSinglePropertyMaxPriceThunk(0));
												dispatch(setSinglePropertySecurityDepositThunk(0));
												dispatch(setSinglePropertyAvailabiltyThunk([]));
												dispatch(setSinglePropertyPossessionDateThunk(""));
												dispatch(setSinglePropertyAgeThunk(0));
												dispatch(setSinglePropertyPlotDimensionsThunk([]));
											}}
											variant="outlined"
										>
											<Text
												variant="body1"
												sx={{
													fontWeight: isSelected ? "500" : "400",
												}}
											>
												{option && option.label}
											</Text>
										</CustomChip>
									);
								})}
							</Box>
						</FormControl>
					)}
				</TextContainer>
			) : null}

			{/* Property Category */}

			{property_transaction_type !== "" ? (
				<TextContainer>
					{!isEditing && (
						<CategoryOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.propertyCategory + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_category_type
									? property_category_type.charAt(0).toUpperCase() + property_category_type.slice(1)
									: "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						// <FormControl
						// 	sx={{
						// 		width: "100%",
						// 		margin: "0.5rem 0rem 0rem 0rem",
						// 	}}
						// >
						// 	<InputLabel id="demo-simple-select-helper-label">{localLang.propertyCategory + "*"}</InputLabel>
						// 	<Select
						// 		labelId="demo-simple-select-helper-label"
						// 		id="demo-simple-select-helper"
						// 		value={property_category_type}
						// 		sx={{
						// 			width: "100%",
						// 		}}
						// 		MenuProps={{
						// 			PaperProps: {
						// 				sx: {
						// 					"& .MuiMenu-list": {
						// 						direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						// 					},
						// 				},
						// 			},
						// 		}}
						// 		label={localLang.propertyCategory + "*"}
						// 		onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
						// 			if (event.target.value === "warehousing") {
						// 				dispatch(setSinglePropertyTypeThunk(["Warehouse"]));
						// 				dispatch(setSinglePropertyCategoryTypeThunk(event.target.value));
						// 				dispatch(setSinglePropertySubTypeThunk(""));
						// 				dispatch(setSinglePropertyMinPriceThunk(0));
						// 				dispatch(setSinglePropertyMaxPriceThunk(0));
						// 				dispatch(setSinglePropertySecurityDepositThunk(0));
						// 				dispatch(setSinglePropertyAvailabiltyThunk([]));
						// 				dispatch(setSinglePropertyPossessionDateThunk(""));
						// 				dispatch(setSinglePropertyAgeThunk(0));
						// 				dispatch(setSinglePropertyUnitFacingThunk([]));
						// 				dispatch(setSinglePropertyLandFacingThunk([]));
						// 				// setFurnishing([]);
						// 				// setPlotDimensions([]);
						// 				// setLandArea(0);
						// 				// setMinSuperBuiltUpArea(0);
						// 				// setMaxSuperBuiltUpArea(0);
						// 				// setMinCarpetArea(0);
						// 				// setMaxCarpetArea(0);
						// 				// setMinBedrooms(0);
						// 				// setMaxBedrooms(0);
						// 				// setMinBathrooms(0);
						// 				// setMaxBathrooms(0);
						// 				// setMinBalconies(0);
						// 				// setMaxBalconies(0);
						// 				// // setMinFloors(0);
						// 				// setMaxFloors(0);
						// 				// setFloorNo(0);
						// 			} else {
						// 				dispatch(setSinglePropertyTypeThunk([]));
						// 				dispatch(setSinglePropertyCategoryTypeThunk(event.target.value));
						// 				dispatch(setSinglePropertySubTypeThunk(""));
						// 				dispatch(setSinglePropertyMinPriceThunk(0));
						// 				dispatch(setSinglePropertyMaxPriceThunk(0));
						// 				dispatch(setSinglePropertySecurityDepositThunk(0));
						// 				dispatch(setSinglePropertyAvailabiltyThunk([]));
						// 				dispatch(setSinglePropertyPossessionDateThunk(""));
						// 				dispatch(setSinglePropertyAgeThunk(0));
						// 				dispatch(setSinglePropertyUnitFacingThunk([]));
						// 				dispatch(setSinglePropertyLandFacingThunk([]));
						// 				// setFurnishing([]);
						// 				// setPlotDimensions([]);
						// 				// setLandArea(0);
						// 				// setMinSuperBuiltUpArea(0);
						// 				// setMaxSuperBuiltUpArea(0);
						// 				// setMinCarpetArea(0);
						// 				// setMaxCarpetArea(0);
						// 				// setMinBedrooms(0);
						// 				// setMaxBedrooms(0);
						// 				// setMinBathrooms(0);
						// 				// setMaxBathrooms(0);
						// 				// setMinBalconies(0);
						// 				// setMaxBalconies(0);
						// 				// // setMinFloors(0);
						// 				// setMaxFloors(0);
						// 				// setFloorNo(0);
						// 			}
						// 		}}
						// 	>
						// 		{property_transaction_type === "ofb" && (
						// 			<MenuItem value="public infrastructure">{localLang.public_infrastructure}</MenuItem>
						// 		)}
						// 		{property_transaction_type === "ofb" && (
						// 			<MenuItem value="joint development">{localLang.joint_development}</MenuItem>
						// 		)}
						// 		{property_transaction_type === "ofb" && (
						// 			<MenuItem value="joint venture">{localLang.joint_venture}</MenuItem>
						// 		)}
						// 		<MenuItem value="residential">{localLang.residential}</MenuItem>
						// 		<MenuItem value="commercial">{localLang.commercial}</MenuItem>
						// 		<MenuItem value="agricultural">{localLang.agricultural}</MenuItem>
						// 		<MenuItem value="warehousing">{localLang.warehousing}</MenuItem>
						// 		<MenuItem value="industrial">{localLang.industrial}</MenuItem>
						// 	</Select>
						// </FormControl>

						<FormControl
							sx={{
								width: "100%",
								// margin: "0.5rem 0rem 0rem 0rem",
								gap: "0.5rem",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<CategoryOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.propertyCategory + "*"}
							</Typography>

							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									flexWrap: "wrap",
								}}
							>
								{[
									{ value: "residential", label: localLang.residential },
									{ value: "commercial", label: localLang.commercial },
									{ value: "agricultural", label: localLang.agricultural },
									{ value: "warehousing", label: localLang.warehousing },
									{ value: "industrial", label: localLang.industrial },
								].map((item) => {
									const isSelected = property_category_type === item.value;
									return (
										<CustomChip
											key={item.value}
											disableRipple
											disableTouchRipple
											disableFocusRipple
											isSelected={isSelected}
											onClick={() => handleCategoryClick(item.value)}
											variant="outlined"
										>
											<Text
												variant="body1"
												sx={{
													fontWeight: isSelected ? "500" : "400",
												}}
											>
												{item.value}
											</Text>
										</CustomChip>
									);
								})}
							</Box>
						</FormControl>
					)}
				</TextContainer>
			) : null}

			{/* Property Type */}

			{property_transaction_type !== "" &&
			property_category_type !== "" &&
			property_category_type !== "public infrastructure" &&
			property_category_type !== "joint development" &&
			property_category_type !== "joint venture" &&
			property_category_type !== "warehousing" ? (
				<TextContainer>
					{" "}
					{!isEditing && (
						<HomeWorkOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.propertyType + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_type ? property_type.map((type: string) => type).join(", ") : "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						// <FormControl
						// 	sx={{
						// 		width: "100%",
						// 		// margin: "0.5rem 0rem 0rem 0rem",
						// 		gap: "0.5rem",
						// 	}}
						// 	error={errorPropertyType}
						// >
						// 	<Typography
						// 		sx={{
						// 			fontFamily: "Poppins",
						// 			fontSize: "14px",
						// 			fontWeight: 500,
						// 			lineHeight: "157%",
						// 			letterSpacing: "0.1px",
						// 		}}
						// 	>
						// 		{localLang.propertyType + "*"}
						// 	</Typography>
						// 	<Select
						// 		labelId="demo-multiple-checkbox-label"
						// 		id="demo-multiple-checkbox"
						// 		value={property_type}
						// 		sx={{
						// 			width: "100%",
						// 		}}
						// 		MenuProps={{
						// 			PaperProps: {
						// 				sx: {
						// 					"& .MuiMenu-list": {
						// 						direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						// 					},
						// 				},
						// 			},
						// 		}}
						// 		// label={localLang.propertyType + "*"}
						// 		displayEmpty
						// 		onChange={handleChangePropertyType}
						// 		renderValue={(selected) => (selected.length === 0 ? localLang.propertyType + "*" : selected.join(", "))}
						// 		// renderValue={(selected) => selected.join(", ")}
						// 	>
						// 		{pTypes?.map((item: { value: string; name: string }, index: number) => (
						// 			<MenuItem
						// 				key={item.value}
						// 				value={item.value}
						// 			>
						// 				<ListItemText primary={item.name} />
						// 			</MenuItem>
						// 		))}
						// 	</Select>
						// 	{errorPropertyType && <FormHelperText>{localLang.please_select_a_property_type}</FormHelperText>}
						// </FormControl>
						<FormControl
							sx={{ width: "100%", gap: "0.5rem" }}
							error={errorPropertyType}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								{" "}
								<HomeWorkOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.propertyType + "*"}
							</Typography>

							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: "0.5rem",
								}}
							>
								{pTypes?.map((item: { value: string; name: string }) => {
									const isSelected = property_type.includes(item.value);

									return (
										<CustomChip
											key={item.value}
											disableRipple
											disableTouchRipple
											disableFocusRipple
											isSelected={isSelected}
											onClick={() => {
												handleChangePropertyType({
													target: {
														value: [item.value],
														name: "property_type",
													},
												} as SelectChangeEvent<string[]>);
											}}
											variant="outlined"
										>
											<Text
												variant="body1"
												sx={{
													fontWeight: isSelected ? "500" : "400",
												}}
											>
												{item.name}
											</Text>
										</CustomChip>
									);
								})}
							</Box>

							{errorPropertyType && <FormHelperText>{localLang.please_select_a_property_type}</FormHelperText>}
						</FormControl>
					)}
				</TextContainer>
			) : null}

			{/* Property Subtype */}

			{/* {(property_category_type == "commercial" ||
				property_category_type == "industrial" ||
				property_category_type == "warehousing") &&
			property_type.length !== 0 ? (
				<TextContainer>
					<DomainAddOutlined
						sx={{
						fontSize: "1.25rem",
							[theme.breakpoints.up("md1190")]: {
								fontSize: "1.5rem",
							},
							color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
						}}
					/>
					{!isEditing ? (
						<React.Fragment>
							<CustomText> {(property_transaction_type === "ofb" ? "OFB" : "Property") + " subtype :"}</CustomText>
							<CustomText>{property_subtype ? property_subtype : ""}</CustomText>
						</React.Fragment>
					) : (
						<FormControl
							sx={{
								width: "100%",
								margin: "0.5rem 0rem 0rem 0rem",
							}}
						>
							<InputLabel id="demo-multiple-checkbox-label">
								{(property_transaction_type === "ofb" ? "OFB" : "Property") + " subtype"}
							</InputLabel>
							<Select
								labelId="demo-multiple-checkbox-label"
								id="demo-multiple-checkbox"
								value={property_subtype}
								sx={{
									width: "100%",
								}}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									dispatch(setSinglePropertySubTypeThunk(event.target.value));
								}}
								label={(property_transaction_type === "ofb" ? "OFB" : "Property") + " subtype"}
							>
								{subtype_options?.map((name: string) => (
									<MenuItem
										key={name}
										value={name}
									>
										<ListItemText primary={name} />
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
				</TextContainer>
			) : null} */}

			{/* Price */}

			{property_transaction_type !== "ofb" &&
			property_transaction_type !== "" &&
			property_category_type !== "" &&
			property_type.length !== 0 ? (
				<TextContainer>
					{!isEditing && (
						<PaymentsOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.priceText + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{(() => {
									const numberSystem =
										property_currency === "AED" ? "international" : "indian";
									const symbol =
										property_currency === "AED" ? (
											<span style={{ fontFamily: "CurrencySymbols", marginRight: "0.25rem" }}>
												{String.fromCharCode(0xe001)}
											</span>
										) : (
											"₹ "
										);
									return propertyData.price.min === propertyData.price.max ? (
										<>
											{symbol}
											{PreparePriceString(propertyData.price.min, numberSystem)}
										</>
									) : (
										<>
											{symbol}
											{PreparePriceString(propertyData.price.min, numberSystem) +
												" - " +
												PreparePriceString(propertyData.price.max, numberSystem)}
										</>
									);
								})()}
							</CustomText>
						</DetailsContainer>
					) : property_transaction_type == "buy" &&
					  !property_type.includes("Independent House") &&
					  !property_type.includes("Land (Residential)") &&
					  !property_type.includes("Land (Commercial)") &&
					  !property_type.includes("Land (Agricultural)") &&
					  !property_type.includes("Land (Industrial)") &&
					  !property_type.includes("Co-living/PG") &&
					  !property_type.includes("Building (Commercial)") &&
					  !property_type.includes("Building (Residential)") &&
					  !property_type.includes("Shop") &&
					  !property_type.includes("Office Space") &&
					  !property_type.includes("Co-working") &&
					  !property_type.includes("Warehouse") &&
					  !property_type.includes("Factory") &&
					  !property_type.includes("Mining") ? (
						<TextContainer
							sx={{
								alignItems: "flex-start",
								flexDirection: "column",
								gap: "0.5rem",
								padding: "0rem",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<PaymentsOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.price}
							</Typography>
							<FormControl
								size="small"
								sx={{
									minWidth: "8rem",
									display: "none",
									"& .MuiOutlinedInput-root": {
										borderRadius: "0.75rem",
									},
								}}
							>
								<Select
									value={property_currency === "AED" ? "AED" : "INR"}
									onChange={(event: SelectChangeEvent) => {
										dispatch(setSinglePropertyCurrencyThunk(event.target.value as "INR" | "AED"));
									}}
									inputProps={{ "aria-label": localLang.currency }}
								>
									<MenuItem value="INR">INR</MenuItem>
									<MenuItem value="AED">AED</MenuItem>
								</Select>
							</FormControl>
							<RowContainer>
								<TextField
									id={screenType + "_spp_property_details_min_price_textfield"}
									value={property_min_price === 0 ? "" : NumberToCurrencyPipe(property_min_price)}
									sx={{
										width: "50%",
										margin: "0rem 0rem 0rem 0rem",
									}}
									slotProps={{
										htmlInput: {
											min: 0,
											inputMode: "numeric",
											pattern: "[0-9]*",
										},
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Box
														onClick={(event: React.MouseEvent<HTMLDivElement>) => {
															const inputRoot = event.currentTarget.closest(".MuiOutlinedInput-root") as HTMLElement | null;
															setCurrencyMenuAnchor(inputRoot ?? event.currentTarget);
														}}
														sx={{
															display: "flex",
															alignItems: "center",
															cursor: "pointer",
															userSelect: "none",
														}}
													>
														{currencySymbol}
														<ArrowDropDownIcon fontSize="small" />
													</Box>
												</InputAdornment>
											),
										},
									}}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										dispatch(setSinglePropertyMinPriceThunk(CurrencyToNumberPipe(event.target.value)));
										if (
											(property_max_price !== 0 && CurrencyToNumberPipe(event.target.value) > property_max_price) ||
											isNaN(CurrencyToNumberPipe(event.target.value))
										) {
											setErrorMinPrice(true);
											setErrorMaxPrice(true);
											setSubmitDisabled(true);
										} else {
											setErrorMinPrice(false);
											setErrorMaxPrice(false);
											setSubmitDisabled(false);
										}
									}}
									onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
										if (event.key === "Enter" || event.key === "Next") {
											(event.target as HTMLInputElement).blur(); // Blur the input field to close the keypad
										}
									}}
									placeholder={localLang.minimum_price + "*"}
									error={errorMinPrice}
									helperText={
										errorMinPrice
											? localLang.value_is_more_than_maximum_price
											: property_min_price > 0
												? PreparePriceString(property_min_price, property_currency === "AED" ? "international" : "indian")
												: ""
									}
									autoComplete="off"
								/>

								<TextField
									id={screenType + "_spp_property_details_max_price_textfield"}
									value={property_max_price === 0 ? "" : NumberToCurrencyPipe(property_max_price)}
									sx={{
										width: "50%",
										margin: "0rem 0rem 0rem 0rem",
									}}
									slotProps={{
										htmlInput: {
											inputMode: "numeric",
											pattern: "[0-9]*",
										},
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Box
														onClick={(event: React.MouseEvent<HTMLDivElement>) => {
															const inputRoot = event.currentTarget.closest(".MuiOutlinedInput-root") as HTMLElement | null;
															setCurrencyMenuAnchor(inputRoot ?? event.currentTarget);
														}}
														sx={{
															display: "flex",
															alignItems: "center",
															cursor: "pointer",
															userSelect: "none",
														}}
													>
														{currencySymbol}
														<ArrowDropDownIcon fontSize="small" />
													</Box>
												</InputAdornment>
											),
										},
									}}
									autoComplete="off"
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										dispatch(setSinglePropertyMaxPriceThunk(CurrencyToNumberPipe(event.target.value)));
										if (
											(property_min_price !== 0 && CurrencyToNumberPipe(event.target.value) < property_min_price) ||
											isNaN(CurrencyToNumberPipe(event.target.value))
										) {
											setErrorMinPrice(true);
											setErrorMaxPrice(true);
											setSubmitDisabled(true);
										} else {
											setErrorMinPrice(false);
											setErrorMaxPrice(false);
											setSubmitDisabled(false);
										}
									}}
									onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
										if (event.key === "Enter" || event.key === "Next") {
											(event.target as HTMLInputElement).blur(); // Blur the input field to close the keypad
										}
									}}
									// label={localLang.maximum_price + "*"}
									placeholder={localLang.maximum_price + "*"}
									error={errorMaxPrice}
									helperText={
										errorMaxPrice
											? localLang.value_is_less_than_minimum_price
											: property_max_price > 0
												? PreparePriceString(property_max_price, property_currency === "AED" ? "international" : "indian")
												: ""
									}
								/>
							</RowContainer>
						</TextContainer>
					) : (
						<TextContainer
							sx={{
								padding: "0rem",
								alignItems: "flex-start",
								flexDirection: "column",
								gap: "0.5rem",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								{" "}
								<PaymentsOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{property_transaction_type == "resale" ||
								(property_transaction_type !== "rent" &&
									(property_type.includes("Independent House") ||
										property_type.includes("Land (Residential)") ||
										property_type.includes("Land (Commercial)") ||
										property_type.includes("Land (Agricultural)") ||
										property_type.includes("Land (Industrial)") ||
										property_type.includes("Co-living/PG") ||
										property_type.includes("Building (Commercial)") ||
										property_type.includes("Building (Residential)") ||
										property_type.includes("Shop") ||
										property_type.includes("Office Space") ||
										property_type.includes("Co-working") ||
										property_type.includes("Warehouse") ||
										property_type.includes("Factory") ||
										property_type.includes("Mining")))
									? localLang.priceText + "*"
									: localLang.monthly_rent + "*"}
							</Typography>
							<FormControl
								size="small"
								sx={{
									minWidth: "8rem",
									display: "none",
									"& .MuiOutlinedInput-root": {
										borderRadius: "0.75rem",
									},
								}}
							>
								<Select
									value={property_currency === "AED" ? "AED" : "INR"}
									onChange={(event: SelectChangeEvent) => {
										dispatch(setSinglePropertyCurrencyThunk(event.target.value as "INR" | "AED"));
									}}
									inputProps={{ "aria-label": localLang.currency }}
								>
									<MenuItem value="INR">INR</MenuItem>
									<MenuItem value="AED">AED</MenuItem>
								</Select>
							</FormControl>
							<TextField
								id={screenType + "_spp_property_details_min_price_textfield"}
								value={property_max_price === 0 ? "" : NumberToCurrencyPipe(property_max_price)}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									dispatch(setSinglePropertyMinPriceThunk(CurrencyToNumberPipe(event.target.value)));
									dispatch(setSinglePropertyMaxPriceThunk(CurrencyToNumberPipe(event.target.value)));
									if (isNaN(CurrencyToNumberPipe(event.target.value))) {
										setErrorMaxPrice(true);
										setSubmitDisabled(true);
									} else {
										setErrorMaxPrice(false);
										setSubmitDisabled(false);
									}
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur(); // Blur the input field to close the keypad
									}
								}}
								autoComplete="off"
								sx={{
									width: "100%",
									[theme.breakpoints.down("sm")]: {
										margin: "0rem 0rem 0rem 0rem",
										minWidth: "100%",
									},
								}}
								placeholder={
									property_transaction_type == "resale" ||
									(property_transaction_type !== "rent" &&
										(property_type.includes("Independent House") ||
											property_type.includes("Land (Residential)") ||
											property_type.includes("Land (Commercial)") ||
											property_type.includes("Land (Agricultural)") ||
											property_type.includes("Land (Industrial)") ||
											property_type.includes("Co-living/PG") ||
											property_type.includes("Building (Commercial)") ||
											property_type.includes("Building (Residential)") ||
											property_type.includes("Shop") ||
											property_type.includes("Office Space") ||
											property_type.includes("Co-working") ||
											property_type.includes("Warehouse") ||
											property_type.includes("Factory") ||
											property_type.includes("Mining")))
										? localLang.priceText + "*"
										: localLang.monthly_rent + "*"
								}
								error={errorMaxPrice}
								helperText={
									errorMaxPrice
										? localLang.enter_a_valid_number
										: property_max_price > 0
											? PreparePriceString(property_max_price, property_currency === "AED" ? "international" : "indian")
											: ""
								}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
									input: {
										startAdornment: (
											<InputAdornment position="start">
												<Box
													onClick={(event: React.MouseEvent<HTMLDivElement>) => {
														const inputRoot = event.currentTarget.closest(".MuiOutlinedInput-root") as HTMLElement | null;
														setCurrencyMenuAnchor(inputRoot ?? event.currentTarget);
													}}
													sx={{
														display: "flex",
														alignItems: "center",
														cursor: "pointer",
														userSelect: "none",
													}}
												>
													{currencySymbol}
													<ArrowDropDownIcon fontSize="small" />
												</Box>
											</InputAdornment>
										),
									},
								}}
							/>
						</TextContainer>
					)}
					<Menu
						anchorEl={currencyMenuAnchor}
						open={Boolean(currencyMenuAnchor)}
						onClose={() => setCurrencyMenuAnchor(null)}
						disableScrollLock
						anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
						transformOrigin={{ vertical: "top", horizontal: "left" }}
						PaperProps={{
							sx: {
								border: "1px solid var(--Stroke-Colour-Stroke-2, #EAEAEA)",
								borderRadius: "0.75rem",
								boxShadow: "0 12px 20px 0 rgba(0, 0, 0, 0.08)",
								padding: "0rem",
								"& .MuiList-root": { padding: "0rem" },
							},
						}}
					>
						{(["INR", "AED"] as const).map((code) => {
							const selected = property_currency === code;
							return (
								<MenuItem
									key={code}
									selected={selected}
									onClick={() => handleCurrencySelect(code)}
									sx={{
										fontSize: "0.825rem",
										fontWeight: selected ? 600 : 400,
										padding: "0.75rem",
										width: "5rem",
										color: "#424242",
										backgroundColor: selected ? "#fff !important" : "transparent",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										gap: "0.5rem",
									}}
								>
									<span>{code}</span>
									{selected && (
										<CheckCircle sx={{ color: theme.palette.success.main, fontSize: "1rem" }} />
									)}
								</MenuItem>
							);
						})}
					</Menu>
				</TextContainer>
			) : null}

			{/* Security Deposit */}

			{property_transaction_type == "rent" && property_category_type !== "" && property_type.length !== 0 ? (
				<TextContainer>
					{!isEditing && (
						<MonetizationOnOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.securityDeposit + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_security_deposit.min ? (
									<>
										{property_security_deposit.currency === "AED" ? (
											<span style={{ fontFamily: "CurrencySymbols", marginRight: "0.25rem" }}>
												{String.fromCharCode(0xe001)}
											</span>
										) : (
											"₹ "
										)}
										{PreparePriceString(
											property_security_deposit.min,
											property_security_deposit.currency === "AED" ? "international" : "indian",
										)}
									</>
								) : (
									"(not filled)"
								)}
							</CustomText>
						</DetailsContainer>
					) : (
						<TextContainer
							sx={{
								padding: "0rem",
								alignItems: "flex-start",
								flexDirection: "column",
								gap: "0.5rem",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<MonetizationOnOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.securityDeposit}
							</Typography>
							<TextField
								id={screenType + "_spp_property_details_security_deposit_textfield"}
								value={property_security_deposit.max === 0 ? "" : NumberToCurrencyPipe(property_security_deposit.max)}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									dispatch(setSinglePropertySecurityDepositThunk(CurrencyToNumberPipe(event.target.value)));
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur(); // Blur the input field to close the keypad
									}
								}}
								autoComplete="off"
								sx={{
									width: "100%",
									[theme.breakpoints.down("sm")]: {
										margin: "0rem 0rem 0.5rem 0rem",
										minWidth: "100%",
									},
								}}
								placeholder={localLang.securityDeposit}
								helperText={
									property_security_deposit.max > 0
										? PreparePriceString(
												property_security_deposit.max,
												property_currency === "AED" ? "international" : "indian",
											)
										: ""
								}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
							/>
						</TextContainer>
					)}
				</TextContainer>
			) : null}

			{/* Currency */}

			{/* {property_transaction_type !== "ofb" &&
			property_transaction_type !== "" &&
			property_category_type !== "" &&
			property_type.length !== 0 &&
			isEditing ? (
				<TextContainer>
					<CurrencyExchange
						sx={{
						fontSize: "1.25rem",
							[theme.breakpoints.up("md1190")]: {
								fontSize: "1.5rem",
							},
							color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
						}}
					/>
					<FormControl
						sx={{
							width: "100%",
							margin: "0.5rem 0rem 0rem 0rem",
						}}
					>
						<InputLabel id="demo-simple-select-helper-label">Currency</InputLabel>
						<Select
							labelId="demo-simple-select-helper-label"
							id="demo-simple-select-helper"
							value={currency}
							sx={{
								width: "100%",
							}}
							label="Currency"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setCurrency(event.target.value);
							}}
						>
							<MenuItem value="INR">INR</MenuItem>
							<MenuItem value="USD">USD</MenuItem>
							<MenuItem value="SGD">SGD</MenuItem>
						</Select>
					</FormControl>
				</TextContainer>
			) : null} */}

			{/* Property Status / Availability */}

			{(property_transaction_type !== "ofb" &&
				property_transaction_type !== "" &&
				property_category_type !== "" &&
				property_type.length !== 0 &&
				isEditing &&
				!property_type.includes("Land (Residential)") &&
				!property_type.includes("Land (Commercial)") &&
				!property_type.includes("Land (Agricultural)") &&
				!property_type.includes("Land (Industrial)") &&
				!property_type.includes("Mining")) ||
			(property_transaction_type == "rent" &&
				property_category_type !== "" &&
				(property_type.includes("Land (Residential)") ||
					property_type.includes("Land (Commercial)") ||
					property_type.includes("Land (Agricultural)") ||
					property_type.includes("Land (Industrial)") ||
					property_type.includes("Mining"))) ? (
				<TextContainer>
					{!isEditing && (
						<EventAvailableOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.propertyStatusTitle + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_availability.length !== 0 ||
								property_availability !== undefined ||
								property_availability !== null
									? property_availability.map(
											(element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "),
										)
									: "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						// <FormControl
						// 	sx={{
						// 		width: "100%",
						// 		gap: "0.5rem",
						// 		// margin: "0.5rem 0rem 0rem 0rem",
						// 	}}
						// 	error={errorAvailability}
						// >
						// 	<Typography
						// 		sx={{
						// 			fontFamily: "Poppins",
						// 			fontSize: "14px",
						// 			fontWeight: 500,
						// 			lineHeight: "157%",
						// 			letterSpacing: "0.1px",
						// 		}}
						// 	>
						// 		{localLang.propertyStatusEdit + "*"}
						// 	</Typography>

						// 	<Select
						// 		labelId="demo-multiple-checkbox-label"
						// 		id="demo-multiple-checkbox"
						// 		value={property_availability.map(
						// 			(element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "),
						// 		)}
						// 		sx={{
						// 			width: "100%",
						// 		}}
						// 		MenuProps={{
						// 			PaperProps: {
						// 				sx: {
						// 					"& .MuiMenu-list": {
						// 						direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						// 					},
						// 				},
						// 			},
						// 		}}
						// 		onChange={(e: SelectChangeEvent<string[]>) => {
						// 			handleChangeAvailability(e);
						// 		}}
						// 		input={<OutlinedInput label={localLang.propertyStatusEdit + "*"} />}
						// 		// renderValue={(selected) => selected.join(", ")}
						// 	>
						// 		{property_transaction_type !== "rent"
						// 			? AvailItems?.map((item: { value: string; name: string }, index: number) => (
						// 					<MenuItem
						// 						key={index}
						// 						value={item.value}
						// 					>
						// 						<ListItemText primary={item.name} />
						// 					</MenuItem>
						// 				))
						// 			: RentAvailabilty?.map((item: { value: string; name: string }, index: number) => (
						// 					<MenuItem
						// 						key={index}
						// 						value={item.value}
						// 					>
						// 						<ListItemText primary={item.name} />
						// 					</MenuItem>
						// 				))}
						// 	</Select>
						// 	{errorAvailability && <FormHelperText>{localLang.property_status_is_required}</FormHelperText>}
						// </FormControl>

						<FormControl
							sx={{
								width: "100%",
								gap: "0.5rem",
							}}
							error={errorAvailability}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<EventAvailableOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.propertyStatusEdit + "*"}
							</Typography>

							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									flexWrap: "wrap",
								}}
							>
								{(property_transaction_type !== "rent" ? AvailItems : RentAvailabilty)?.map(
									(item: { value: string; name: string }, index: number) => {
										const isSelected = property_availability?.[0] === item.value;

										return (
											<CustomChip
												key={index}
												disableRipple
												disableTouchRipple
												disableFocusRipple
												isSelected={isSelected}
												onClick={() => {
													const selectedValue = item.value;

													handleChangeAvailability({
														target: {
															value: selectedValue ? [selectedValue] : [],
															name: "property-availability",
														},
													} as unknown as SelectChangeEvent<string[]>);

													setErrorAvailability(false);
													if (
														item.value === "Select date" ||
														item.value === "select_date" ||
														item.value === "Under construction" ||
														item.value === "under_construction"
													) {
														setIsDatePickerOpen(true);
													}
												}}
												variant="outlined"
											>
												<Text
													variant="body1"
													sx={{
														fontWeight: isSelected ? "500" : "400",
													}}
												>
													{item.name === "Select date" ||
													item.name === "select_date" ||
													item.name === "Under construction" ||
													item.name === "under_construction"
														? property_possession_date === "" ||
															property_possession_date === null ||
															property_possession_date === undefined
															? item.name
															: dayjs(property_possession_date).format("DD/MM/YYYY")
														: item.name}
												</Text>
											</CustomChip>
										);
									},
								)}
							</Box>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DatePicker
									value={dayjs(property_possession_date)}
									onChange={(date: Dayjs | null) => {
										if (date) {
											dispatch(setSinglePropertyPossessionDateThunk(date.toISOString()));
											setIsDatePickerOpen(false);
										}
									}}
									disablePast
									format="DD/MM/YYYY"
									open={isDatePickerOpen}
									onClose={() => setIsDatePickerOpen(false)}
									onOpen={() => setIsDatePickerOpen(true)}
									sx={{ display: "none", width: "100%" }}
									slotProps={{
										popper: {
											anchorEl: {
												getBoundingClientRect: () => ({
													top: window.innerHeight / 2,
													left: window.innerWidth / 2,
													width: 0,
													height: 0,
													right: window.innerWidth / 2,
													bottom: window.innerHeight / 2,
													x: window.innerWidth / 2,
													y: window.innerHeight / 2,
													toJSON: () => {},
												}),
											},
											modifiers: [
												{
													name: "offset",
													options: {
														offset: [-150, -150], // Adjust to shift more precisely
													},
												},
											],
										},
									}}
								/>
							</LocalizationProvider>
							{errorAvailability && <FormHelperText>{localLang.property_status_is_required}</FormHelperText>}
						</FormControl>
					)}
				</TextContainer>
			) : null}

			{/* Possession Date */}

			{/* {property_transaction_type !== "" &&
			property_category_type !== "" &&
			property_type.length !== 0 &&
			(property_availability.includes("Under construction") ||
				property_availability.includes("under_construction") ||
				property_availability.includes("Select date") ||
				property_availability.includes("select_date")) ? (
				<TextContainer>
					{!isEditing && (
						<CalendarMonthOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.posssesionDate + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_possession_date !== "" ||
								property_possession_date !== undefined ||
								property_possession_date !== null
									? new Date(property_possession_date).toLocaleDateString("en-IN")
									: "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						<TextContainer
							sx={{
								flexDirection: "column",
								alignItems: "flex-start",
								width: "100%",
								gap: "0.5rem ",
							}}
						>
							{" "}
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<CalendarMonthOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.posssesionDate + "*"}
							</Typography>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DatePicker
									// label={localLang.posssesionDate + "*"}
									value={dayjs(property_possession_date)}
									// onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									// 	dispatch(setSinglePropertyPossessionDateThunk(new Date(e)));
									// }}
									onChange={(date: Dayjs | null) => {
										if (date) {
											dispatch(setSinglePropertyPossessionDateThunk(date.toDate()));
											setIsDatePickerOpen(false);
										}
									}}
									open={isDatePickerOpen}
									onClose={() => setIsDatePickerOpen(false)}
									onOpen={() => setIsDatePickerOpen(true)}
									disablePast
									format="DD/MM/YYYY"
									sx={{ width: "100%" }}
								/>
							</LocalizationProvider>
						</TextContainer>
					)}
				</TextContainer>
			) : null} */}

			{/* Property Age */}

			{property_transaction_type == "resale" &&
			property_category_type !== "" &&
			property_type.length !== 0 &&
			!property_type.includes("Land (Residential)") &&
			!property_type.includes("Land (Commercial)") &&
			!property_type.includes("Land (Agricultural)") &&
			!property_type.includes("Land (Industrial)") &&
			!property_type?.includes("Plot") &&
			!property_type?.includes("Farm Plot") &&
			!property_type?.includes("Plot (Commercial)") &&
			!property_type.includes("Mining") &&
			!property_availability.includes("Under construction") &&
			!property_availability.includes("under_construction") &&
			!property_availability.includes("Select date") &&
			!property_availability.includes("select_date") ? (
				<TextContainer>
					{!isEditing && (
						<HourglassEmptyOutlined
							sx={{
								fontSize: "1.25rem",
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1.5rem",
								},
								color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
							}}
						/>
					)}
					{!isEditing ? (
						<DetailsContainer>
							<CustomText>{localLang.propertyAge + " :"}</CustomText>
							<CustomText
								sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
							>
								{property_age !== undefined || property_age !== null || property_age === 0
									? property_age + " " + localLang.years
									: "(not filled)"}
							</CustomText>
						</DetailsContainer>
					) : (
						<TextContainer
							sx={{
								flexDirection: "column",
								alignItems: "flex-start",
								width: "100%",
								gap: "0.5rem ",
							}}
						>
							<Typography
								sx={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "flex-start",
									alignItems: "center",
									gap: "0.5rem",
									fontFamily: "Poppins",
									fontSize: "14px",
									fontWeight: 500,
									lineHeight: "157%",
									letterSpacing: "0.1px",
								}}
							>
								<HourglassEmptyOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
										color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
									}}
								/>
								{localLang.propertyAge}
							</Typography>
							<TextField
								id={screenType + "_spp_property_details_age_textfield"}
								value={property_age === 0 ? "" : property_age}
								slotProps={{
									htmlInput: {
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								autoComplete="off"
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									dispatch(setSinglePropertyAgeThunk(Number(event.target.value.replace(/\D/g, ""))));
									if (isNaN(Number(event.target.value.replace(/\D/g, "")))) {
										setErrorPropertyAge(true);
										setSubmitDisabled(true);
									} else {
										setErrorPropertyAge(false);
										setSubmitDisabled(false);
									}
								}}
								sx={{
									width: "100%",
								}}
								error={errorPropertyAge}
								helperText={errorPropertyAge ? localLang.enter_a_valid_number : ""}
								label={localLang.propertyAgeInYears + "*"}
							/>
						</TextContainer>
					)}
				</TextContainer>
			) : null}

			{/* Unit Facing */}

			{((property_transaction_type !== "" &&
				property_category_type !== "" &&
				property_type?.length !== 0 &&
				router.locale !== "ar-AE" &&
				!property_type?.includes("Land (Residential)") &&
				!property_type?.includes("Land (Commercial)") &&
				!property_type?.includes("Land (Agricultural)") &&
				!property_type?.includes("Land (Industrial)") &&
				!property_type?.includes("Warehouse") &&
				!property_type?.includes("Factory") &&
				!property_type?.includes("Mining") &&
				!property_type?.includes("Office Space") &&
				!property_type?.includes("Co-working")) ||
				(property_transaction_type === "buy" &&
					property_category_type !== "" &&
					property_type?.length !== 0 &&
					router.locale !== "ar-AE" &&
					!property_type?.includes("Land (Residential)") &&
					!property_type?.includes("Land (Commercial)") &&
					!property_type?.includes("Land (Agricultural)") &&
					!property_type?.includes("Land (Industrial)") &&
					!property_type?.includes("Warehouse") &&
					!property_type?.includes("Factory") &&
					!property_type?.includes("Mining")) ||
				(property_transaction_type === "resale" &&
					property_category_type !== "" &&
					property_type?.length !== 0 &&
					router.locale !== "ar-AE" &&
					!property_type?.includes("Land (Residential)") &&
					!property_type?.includes("Land (Commercial)") &&
					!property_type?.includes("Land (Agricultural)") &&
					!property_type?.includes("Land (Industrial)") &&
					!property_type?.includes("Warehouse") &&
					!property_type?.includes("Factory") &&
					!property_type?.includes("Mining")) ||
				(property_transaction_type === "rent" &&
					property_category_type !== "" &&
					property_type?.length !== 0 &&
					router.locale !== "ar-AE" &&
					property_type?.includes("Office Space"))) &&
				(!isEditing ? (
					<DetailsContainer>
						<div
							style={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "flex-start",
								alignItems: "center",
								gap: "0.5rem",
							}}
						>
							<ExploreOutlinedIcon
								sx={{
									fontSize: "1.25rem",
									[theme.breakpoints.up("md1190")]: {
										fontSize: "1.5rem",
									},
									color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
								}}
							/>
							<CustomText>{localLang.facing + " :"}</CustomText>
						</div>
						<CustomText
							sx={{ fontWeight: 500, fontSize: "1rem", [theme.breakpoints.down("sm")]: { fontSize: "0.875rem" } }}
						>
							{property_unit_facing.length !== 0
								? property_unit_facing
										.map((element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "))
										.join(", ")
								: "(not filled)"}
						</CustomText>
					</DetailsContainer>
				) : (
					<FormControl
						sx={{
							width: "100%",
							gap: "0.5rem",
						}}
					>
						<Typography
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "flex-start",
								alignItems: "center",
								gap: "0.5rem",
								fontFamily: "Poppins",
								fontSize: "14px",
								fontWeight: 500,
								lineHeight: "157%",
								letterSpacing: "0.1px",
							}}
						>
							<ExploreOutlinedIcon
								sx={{
									fontSize: "1.25rem",
									[theme.breakpoints.up("md1190")]: {
										fontSize: "1.5rem",
									},
									color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
								}}
							/>
							{localLang.facing}
						</Typography>

						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							{Facing.map((item, index) => {
								const isSelected = property_unit_facing.includes(item.value);

								const isMultiSelect =
									property_transaction_type === "buy" &&
									(property_type.includes("Apartment") ||
										property_type.includes("Plot") ||
										property_type.includes("Farm Plot") ||
										property_type.includes("Plot (Commercial)") ||
										property_type.includes("Row House") ||
										property_type.includes("Villa") ||
										property_type.includes("Builder Floor Apartment") ||
										property_type.includes("Penthouse"));

								const handleChipClick = () => {
									let updatedValues: string[];

									if (isMultiSelect) {
										updatedValues = isSelected
											? property_unit_facing.filter((val) => val !== item.value)
											: [...property_unit_facing, item.value];
									} else {
										updatedValues = isSelected ? [] : [item.value];
									}

									handleChangeUnitFacing({
										target: {
											value: updatedValues,
											name: "property-unit-facing",
										},
									} as unknown as SelectChangeEvent<string[]>);
								};

								return (
									<CustomChip
										key={index}
										disableRipple
										disableTouchRipple
										disableFocusRipple
										isSelected={isSelected}
										onClick={handleChipClick}
										variant="outlined"
									>
										<Text
											variant="body1"
											sx={{
												fontWeight: isSelected ? "500" : "400",
											}}
										>
											{item.name}
										</Text>
									</CustomChip>
								);
							})}
						</Box>
					</FormControl>
				))}

			{/* Update Button */}

			{isEditing ? (
				<>
					{load ? (
						<CircularProgress
							// size={24}
							sx={{
								marginTop: "1rem",
							}}
						/>
					) : (
						<BeegruButton
							id={screenType + "_spp_property_details_update_button"}
							flavor="primary"
							onClick={() => {
								if (
									property_transaction_type !== "ofb" &&
									(property_transaction_type === "" ||
										property_category_type === "" ||
										property_type.length === 0 ||
										property_min_price === 0 ||
										property_min_price === null ||
										property_max_price === 0 ||
										property_max_price === null ||
										(!property_type.includes("Land (Residential)") &&
											!property_type.includes("Land (Commercial)") &&
											!property_type.includes("Land (Agricultural)") &&
											!property_type.includes("Land (Industrial)") &&
											!property_type.includes("Mining") &&
											property_availability.length === 0) ||
										((property_availability.includes("Under construction") ||
											property_availability.includes("under_construction") ||
											property_availability.includes("Select date") ||
											property_availability.includes("select_date")) &&
											property_possession_date === "") ||
										(property_transaction_type == "resale" &&
											!property_type?.includes("Land (Residential)") &&
											!property_type?.includes("Land (Commercial)") &&
											!property_type?.includes("Land (Agricultural)") &&
											!property_type?.includes("Land (Industrial)") &&
											!property_type?.includes("Plot") &&
											!property_type?.includes("Farm Plot") &&
											!property_type?.includes("Plot (Commercial)") &&
											!property_type?.includes("Mining") &&
											!property_availability?.includes("Under construction") &&
											!property_availability?.includes("under_construction") &&
											!property_availability?.includes("Select date") &&
											!property_availability?.includes("select_date") &&
											(property_age === 0 || property_age === null)) ||
										(property_transaction_type == "rent" && property_availability.length === 0))
								) {
									if (property_transaction_type === "") {
										setErrorTransactionType(true);
									}
									if (property_category_type === "") {
										setErrorCategoryType(true);
									}
									if (property_type.length === 0) {
										setErrorPropertyType(true);
									}
									if (property_min_price === 0 || property_min_price === null) {
										setErrorMinPrice(true);
									}
									if (property_max_price === 0 || property_max_price === null) {
										setErrorMaxPrice(true);
									}
									if (
										property_transaction_type === "rent" &&
										(property_type.includes("Land (Residential)") ||
											property_type.includes("Land (Commercial)") ||
											property_type.includes("Land (Agricultural)") ||
											property_type.includes("Land (Industrial)") ||
											property_type.includes("Mining")) &&
										property_availability.length === 0
									) {
										setErrorAvailability(true);
									}
									if (
										!property_type.includes("Land (Residential)") &&
										!property_type.includes("Land (Commercial)") &&
										!property_type.includes("Land (Agricultural)") &&
										!property_type.includes("Land (Industrial)") &&
										!property_type.includes("Mining") &&
										property_availability.length === 0
									) {
										setErrorAvailability(true);
									}
									if (
										(property_availability.includes("Under construction") ||
											property_availability.includes("under_construction") ||
											property_availability.includes("Select date") ||
											property_availability.includes("select_date")) &&
										property_possession_date === ""
									) {
										setErrorPossessionDate(true);
									}
									if (
										property_transaction_type == "resale" &&
										!property_type?.includes("Land (Residential)") &&
										!property_type?.includes("Land (Commercial)") &&
										!property_type?.includes("Land (Agricultural)") &&
										!property_type?.includes("Land (Industrial)") &&
										!property_type?.includes("Plot") &&
										!property_type?.includes("Farm Plot") &&
										!property_type?.includes("Plot (Commercial)") &&
										!property_type?.includes("Mining") &&
										!property_availability?.includes("Under construction") &&
										!property_availability?.includes("under_construction") &&
										!property_availability?.includes("Select date") &&
										!property_availability?.includes("select_date") &&
										(property_age === 0 || property_age === null)
									) {
										setErrorPropertyAge(true);
									}
									if (property_transaction_type === "rent" && property_availability.length === 0) {
										setErrorAvailability(true);
									}

									// setSubmitDisabled(true);
								} else {
									submitForm();
								}
							}}
							sx={{ marginTop: "1.125rem", width: "100%" }}
							variant="contained"
							disabled={submitDisabled}
						>
							{localLang.update}
						</BeegruButton>
					)}
				</>
			) : null}
		</PaperContainer>
	);
};

export default PropertyCard;
